import { Box, Card, Chip, CircularProgress, Collapse, Divider, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import FlexBox from 'components/flexbox/FlexBox';
import { H1, H2, H3 } from 'components/Typography';
import numeral from 'numeral';
import { type FC, useState } from 'react';
import 'numeral/locales/it';
import { TFunction } from 'i18next';
import { Agreements } from 'icons/report/reactIcons';
import { AgreementEntity, UpdateAgreementDto } from 'api/generated';
import { agreementApi } from 'api';
import { decryptData, encryptData } from 'utils/encryptionDecryptionAgreements';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import CalendarInput from 'components/input-fields/CalendarInput';

numeral.locale('it');

interface DataProps {
    agreements: {
        avgNetDaily: number,
        monthly: number,
        total: number
    },
    agreementsList?: AgreementEntity[];
    encryptionKey?: string;
    onUpdate?: () => void;
    t: TFunction;
}

const EmployeeAgreement: FC<DataProps> = (props) => {
    const { agreements, agreementsList, encryptionKey, onUpdate, t } = props;
    const [expanded, setExpanded] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [editStartDate, setEditStartDate] = useState<Date | null>(null);
    const [editEndDate, setEditEndDate] = useState<Date | null>(null);
    const [editNetDaily, setEditNetDaily] = useState<string>('');
    const [isSaving, setIsSaving] = useState(false);

    const startEdit = (agreement: AgreementEntity) => {
        setEditingId(agreement.id);
        setEditStartDate(new Date(agreement.startedOn));
        setEditEndDate(agreement.endedOn ? new Date(agreement.endedOn) : null);
        let netDaily = 0;
        if (agreement.net_daily_encrypted && encryptionKey) {
            const decrypted = decryptData(agreement.net_daily_encrypted, encryptionKey);
            if (decrypted !== '') netDaily = Number(decrypted);
        } else if (agreement.net_daily) {
            netDaily = agreement.net_daily;
        }
        setEditNetDaily(netDaily.toString());
    };

    const cancelEdit = () => {
        setEditingId(null);
        setEditStartDate(null);
        setEditEndDate(null);
        setEditNetDaily('');
    };

    const handleSave = async (agreement: AgreementEntity) => {
        setIsSaving(true);
        try {
            const netDailyValue = parseFloat(editNetDaily);
            const updateDto: UpdateAgreementDto = {
                startedOn: editStartDate ? new Date(Date.UTC(editStartDate.getFullYear(), editStartDate.getMonth(), editStartDate.getDate())).toISOString() : agreement.startedOn,
                endedOn: editEndDate ? new Date(Date.UTC(editEndDate.getFullYear(), editEndDate.getMonth(), editEndDate.getDate())).toISOString() : undefined,
            };
            if (!isNaN(netDailyValue)) {
                if (encryptionKey) {
                    updateDto.net_daily_encrypted = encryptData(netDailyValue, encryptionKey);
                } else {
                    updateDto.net_daily = netDailyValue;
                }
            }
            await agreementApi.update(agreement.id.toString(), updateDto);
            setEditingId(null);
            onUpdate?.();
        } catch (error) {
            console.error('Failed to update agreement:', error);
        } finally {
            setIsSaving(false);
        }
    };

    const getNetDaily = (agreement: AgreementEntity): number | null => {
        if (agreement.net_daily_encrypted && encryptionKey) {
            const decrypted = decryptData(agreement.net_daily_encrypted, encryptionKey);
            if (decrypted !== '') return Number(decrypted);
            return null;
        }
        return agreement.net_daily ?? null;
    };

    return (
        <Card
            sx={{
                padding: 3,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <Box width='100%' textAlign='center'>
                <H2 fontWeight={600} mb={3}>
                    {t('employees.report.agreement.title')}
                </H2>
                <Box
                    sx={{
                        width: 40,
                        height: 40,
                        margin: 'auto',
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#5479F7',
                    }}
                >
                    <Agreements />
                </Box>

                <H1 color='primary.main' my={1}>
                    {!isNaN(agreements.total) ? numeral(agreements.monthly).format('0,0$') : '-'} / {t('employees.report.agreement.month')}
                </H1>
                <H2 color='#FF9C12' my={1}>
                    {!isNaN(agreements.total) ? numeral(agreements.avgNetDaily).format('0,0.00$') : '-'} / {t('employees.report.agreement.day')}
                </H2>


                <FlexBox alignItems='center' justifyContent='center'>
                    <H3 color='success.main' fontWeight={600} ml={0.5} mb={0}>
                        {t('employees.report.agreement.total')} {!isNaN(agreements.total) ? numeral(agreements.total).format('0,0$') : '-'}
                    </H3>
                </FlexBox>

                {agreementsList && agreementsList.length > 0 && (
                    <Box mt={2}>
                        <Tooltip title={expanded ? '' : t('employees.agreements.dataTable.modify')}>
                            <IconButton
                                size="small"
                                onClick={() => setExpanded(!expanded)}
                                sx={{ transition: 'transform 0.2s' }}
                            >
                                {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                        </Tooltip>
                        <Collapse in={expanded}>
                            <Divider sx={{ my: 1 }} />
                            {agreementsList
                                .sort((a, b) => new Date(a.startedOn).getTime() - new Date(b.startedOn).getTime())
                                .map((agreement, index) => {
                                    const isEditingThis = editingId === agreement.id;
                                    const netDaily = getNetDaily(agreement);
                                    const isEncrypted = netDaily === null && !!agreement.net_daily_encrypted;

                                    return (
                                        <Box
                                            key={agreement.id}
                                            sx={{
                                                p: 1.5,
                                                mb: 1,
                                                borderRadius: 1,
                                                bgcolor: 'background.default',
                                                textAlign: 'left',
                                            }}
                                        >
                                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                                                <Chip
                                                    label={`#${index + 1}`}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: 'primary.main',
                                                        color: 'white',
                                                        fontWeight: 700,
                                                        fontSize: '0.7rem',
                                                        height: 22,
                                                    }}
                                                />
                                                {isEditingThis ? (
                                                    <Box>
                                                        <IconButton size="small" onClick={cancelEdit} disabled={isSaving}>
                                                            <CloseIcon fontSize="small" />
                                                        </IconButton>
                                                        <IconButton size="small" onClick={async () => handleSave(agreement)} disabled={isSaving} color="primary">
                                                            {isSaving ? <CircularProgress size={16} /> : <SaveIcon fontSize="small" />}
                                                        </IconButton>
                                                    </Box>
                                                ) : (
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => startEdit(agreement)}
                                                        disabled={isEncrypted}
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                )}
                                            </Box>

                                            {isEditingThis ? (
                                                <Box display="flex" flexDirection="column" gap={1}>
                                                    <CalendarInput
                                                        format="dd/MM/yyyy"
                                                        label={t('employees.agreements.field.startDate')}
                                                        value={editStartDate}
                                                        onChange={(v) => v && !isNaN(new Date(v).getTime()) && setEditStartDate(v)}
                                                        slotProps={{ textField: { size: 'small', fullWidth: true } }}
                                                    />
                                                    <CalendarInput
                                                        format="dd/MM/yyyy"
                                                        label={t('employees.agreements.field.endDate')}
                                                        value={editEndDate}
                                                        minDate={editStartDate || undefined}
                                                        onChange={(v) => v && !isNaN(new Date(v).getTime()) && setEditEndDate(v)}
                                                        slotProps={{ textField: { size: 'small', fullWidth: true } }}
                                                    />
                                                    <TextField
                                                        size="small"
                                                        fullWidth
                                                        label={t('employees.agreements.field.net')}
                                                        type="number"
                                                        value={editNetDaily}
                                                        onChange={(e) => setEditNetDaily(e.target.value)}
                                                        InputProps={{ endAdornment: <Typography variant="caption">€</Typography> }}
                                                    />
                                                </Box>
                                            ) : (
                                                <Box display="flex" flexDirection="column" gap={0.3}>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {new Date(agreement.startedOn).toLocaleDateString('it-IT')}
                                                        {agreement.endedOn ? ` → ${new Date(agreement.endedOn).toLocaleDateString('it-IT')}` : ` → ${t('employees.contract.types.unlimited')}`}
                                                    </Typography>
                                                    <Typography variant="body2" fontWeight={600} color={isEncrypted ? 'text.disabled' : 'success.main'}>
                                                        {isEncrypted ? '🔒' : netDaily !== null ? `${numeral(netDaily).format('0,0.00$')} / ${t('employees.report.agreement.day')}` : '-'}
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Box>
                                    );
                                })}
                        </Collapse>
                    </Box>
                )}
            </Box>
        </Card>
    );
};

export default EmployeeAgreement;
